import formatUuidT from "../../../flatbuffers/display/uuid";
import { LiftType } from "../../../constants/lifts";
import { MapViewDirections, MapViewTypes } from "../../../interfaces/MapView.interface";
import {
  DEFAULT_BANK_WAYPOINT_VIEW,
  DEFAULT_FONT_COLOR,
  DEFAULT_FONT_SIZE,
  DEFAULT_GROUP_WAYPOINT_VIEW,
  DEFAULT_WAYPOINT_VIEW,
} from "../../mapViewManagement/mapEditor/constants";
import type {
  BankWaypoint,
  GroupWaypoint,
  Waypoint,
} from "../../mapViewManagement/mapEditor/types";

const BASE_LIFT_SIZE = 46;

const asArray = <T = any>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

const toNumberOrNull = (value: unknown): number | null => {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

function resolveLiftTypeLabel(value: unknown): "elevator" | "escalator" | "movingwalk" | "map" {
  if (typeof value === "string" && value.toLowerCase() === "map") {
    return "map";
  }

  if (typeof value === "number") {
    if (value === LiftType.ESCALATOR) {
      return "escalator";
    }
    if (value === LiftType.MOVINGWALK) {
      return "movingwalk";
    }
    return "elevator";
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return "elevator";
    }

    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) {
      return resolveLiftTypeLabel(parsed);
    }

    if (trimmed.toUpperCase() === LiftType[LiftType.ESCALATOR]) {
      return "escalator";
    }
    if (trimmed.toUpperCase() === LiftType[LiftType.MOVINGWALK]) {
      return "movingwalk";
    }
    if (trimmed.toUpperCase() === LiftType[LiftType.ELEVATOR]) {
      return "elevator";
    }
  }

  return "elevator";
}

function normalizeWaypointFromMapData(waypoint: any): Waypoint {
  return {
    ...DEFAULT_WAYPOINT_VIEW,
    ...waypoint,
    id: formatUuidT(waypoint?.id),
    mapId: formatUuidT(waypoint?.mapId ?? waypoint?.map_id),
    position: typeof waypoint?.position === "number" ? waypoint.position : 0,
    nickname: String(waypoint?.nickname ?? ""),
    x: toNumberOrNull(waypoint?.x),
    y: toNumberOrNull(waypoint?.y),
    displayName: Boolean(waypoint?.display_name ?? waypoint?.displayName ?? true),
    fontSize: Number(waypoint?.font_size ?? waypoint?.fontSize ?? DEFAULT_FONT_SIZE),
    fontColorHex: String(waypoint?.font_color_hex ?? waypoint?.fontColorHex ?? DEFAULT_FONT_COLOR),
  };
}

function normalizeBankWaypointFromMapData(bankWaypoint: any): BankWaypoint {
  const normalizedLifts = asArray(bankWaypoint?.lifts).map((lift) =>
    normalizeWaypointFromMapData(lift),
  );

  return {
    ...DEFAULT_BANK_WAYPOINT_VIEW,
    ...DEFAULT_GROUP_WAYPOINT_VIEW,
    ...bankWaypoint,
    id: formatUuidT(bankWaypoint?.id),
    mapId: formatUuidT(bankWaypoint?.mapId ?? bankWaypoint?.map_id),
    position: typeof bankWaypoint?.position === "number" ? bankWaypoint.position : 0,
    nickname: String(bankWaypoint?.nickname ?? ""),
    x: toNumberOrNull(bankWaypoint?.x),
    y: toNumberOrNull(bankWaypoint?.y),
    displayName: Boolean(bankWaypoint?.display_name ?? bankWaypoint?.displayName ?? true),
    fontSize: Number(bankWaypoint?.font_size ?? bankWaypoint?.fontSize ?? DEFAULT_FONT_SIZE),
    fontColorHex: String(
      bankWaypoint?.font_color_hex ?? bankWaypoint?.fontColorHex ?? DEFAULT_FONT_COLOR,
    ),
    minX: toNumberOrNull(bankWaypoint?.min_x ?? bankWaypoint?.minX),
    maxX: toNumberOrNull(bankWaypoint?.max_x ?? bankWaypoint?.maxX),
    minY: toNumberOrNull(bankWaypoint?.min_y ?? bankWaypoint?.minY),
    maxY: toNumberOrNull(bankWaypoint?.max_y ?? bankWaypoint?.maxY),
    linkLifts: Boolean(bankWaypoint?.link_lifts ?? bankWaypoint?.linkLifts ?? true),
    lifts: normalizedLifts,
    banks: asArray(bankWaypoint?.banks).map((bank) => normalizeBankWaypointFromMapData(bank)),
    bankName: bankWaypoint?.bank_name ?? bankWaypoint?.bankName,
  };
}

function normalizeGroupWaypointFromMapData(groupWaypoint: any): GroupWaypoint {
  const normalizedLifts = asArray(groupWaypoint?.lifts).map((lift) =>
    normalizeWaypointFromMapData(lift),
  );
  const normalizedBanks = asArray(groupWaypoint?.banks).map((bank) =>
    normalizeBankWaypointFromMapData(bank),
  );

  return {
    ...DEFAULT_GROUP_WAYPOINT_VIEW,
    ...groupWaypoint,
    id: formatUuidT(groupWaypoint?.id),
    mapId: formatUuidT(groupWaypoint?.mapId ?? groupWaypoint?.map_id),
    position: typeof groupWaypoint?.position === "number" ? groupWaypoint.position : 0,
    nickname: String(groupWaypoint?.nickname ?? ""),
    x: toNumberOrNull(groupWaypoint?.x),
    y: toNumberOrNull(groupWaypoint?.y),
    displayName: Boolean(groupWaypoint?.display_name ?? groupWaypoint?.displayName ?? true),
    fontSize: Number(groupWaypoint?.font_size ?? groupWaypoint?.fontSize ?? DEFAULT_FONT_SIZE),
    fontColorHex: String(
      groupWaypoint?.font_color_hex ?? groupWaypoint?.fontColorHex ?? DEFAULT_FONT_COLOR,
    ),
    minX: toNumberOrNull(groupWaypoint?.min_x ?? groupWaypoint?.minX),
    maxX: toNumberOrNull(groupWaypoint?.max_x ?? groupWaypoint?.maxX),
    minY: toNumberOrNull(groupWaypoint?.min_y ?? groupWaypoint?.minY),
    maxY: toNumberOrNull(groupWaypoint?.max_y ?? groupWaypoint?.maxY),
    lifts: normalizedLifts,
    banks: normalizedBanks,
    additionalLiftKeys: asArray(
      groupWaypoint?.additionalLiftKeys ?? groupWaypoint?.additional_lift_keys,
    ).map((liftKey) => String(liftKey)),
  };
}

const collectBankLifts = (bankWaypoints: BankWaypoint[]): Waypoint[] =>
  bankWaypoints.flatMap((bankWaypoint) => asArray<Waypoint>(bankWaypoint?.lifts));

const collectGroupLifts = (groupWaypoints: GroupWaypoint[]): Waypoint[] => {
  const groupLevel = groupWaypoints.flatMap((groupWaypoint) =>
    asArray<Waypoint>(groupWaypoint?.lifts),
  );
  const groupBankLevel = groupWaypoints.flatMap((groupWaypoint) =>
    asArray<BankWaypoint>(groupWaypoint?.banks).flatMap((bankWaypoint) =>
      asArray<Waypoint>(bankWaypoint?.lifts),
    ),
  );
  return [...groupLevel, ...groupBankLevel];
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

export function resolveMapCoordinate(value: unknown, axisSize: number): number | null {
  const numeric = toNumberOrNull(value);
  if (numeric === null) {
    return null;
  }

  // Support both legacy relative [0..1] and absolute SVG-space coordinates.
  if (numeric >= 0 && numeric <= 1) {
    return numeric * axisSize;
  }

  return numeric;
}

export interface LiveMapLift {
  id: string;
  text: string;
  x: number | null;
  y: number | null;
  direction: MapViewDirections;
  type: MapViewTypes;
  liftType: string;
  displayName: boolean;
  scale: number;
  backgroundColorHex: string;
  backgroundOpacity: number;
  titleFontSize: number;
  titleColorHex: string;
  titleRotationDeg: number;
  backgroundWidth: number;
  backgroundHeight: number;
}

export function extractMapRenderData(mapValue: any): {
  groupsAndBanks: GroupWaypoint[];
  liveLifts: LiveMapLift[];
} {
  const rawWaypoints = asArray(mapValue?.waypoints);
  const rawGroupWaypoints = asArray(
    mapValue?.group_waypoints ?? mapValue?.groupWaypoints ?? mapValue?.groups,
  );
  const rawBankWaypoints = asArray(
    mapValue?.bank_waypoints ?? mapValue?.bankWaypoints ?? mapValue?.banks,
  );

  const waypoints = rawWaypoints.map((waypoint) => normalizeWaypointFromMapData(waypoint));
  const groupWaypoints = rawGroupWaypoints.map((groupWaypoint) =>
    normalizeGroupWaypointFromMapData(groupWaypoint),
  );
  const bankWaypoints = rawBankWaypoints.map((bankWaypoint) =>
    normalizeBankWaypointFromMapData(bankWaypoint),
  );

  const groupsAndBanks: GroupWaypoint[] = [...groupWaypoints, ...bankWaypoints];
  const allLiftCandidates = [
    ...waypoints,
    ...collectBankLifts(bankWaypoints),
    ...collectGroupLifts(groupWaypoints),
  ];

  const seen = new Set<string>();

  const liveLifts = allLiftCandidates
    .map((waypoint): LiveMapLift | null => {
      const id = formatUuidT(
        waypoint?.linkedLift ?? (waypoint as any)?.linked_lift ?? waypoint?.id,
      );
      if (!id) {
        return null;
      }

      const x = toNumberOrNull(waypoint?.x);
      const y = toNumberOrNull(waypoint?.y);
      const signature = `${id}:${String(x)}:${String(y)}`;
      if (seen.has(signature)) {
        return null;
      }
      seen.add(signature);

      const backgroundWidth = Number(
        waypoint?.backgroundWidth ?? (waypoint as any)?.background_width ?? BASE_LIFT_SIZE,
      );
      const backgroundHeight = Number(
        waypoint?.backgroundHeight ?? (waypoint as any)?.background_height ?? BASE_LIFT_SIZE,
      );
      const scale = clamp(Math.max(backgroundWidth, backgroundHeight) / BASE_LIFT_SIZE, 0.25, 4);

      return {
        id,
        text: String(waypoint?.nickname ?? ""),
        x,
        y,
        direction: ((waypoint as any)?.direction ?? MapViewDirections.UP) as MapViewDirections,
        type: (waypoint?.type ?? MapViewTypes.LIFT) as MapViewTypes,
        liftType: resolveLiftTypeLabel(waypoint?.type),
        displayName: Boolean(waypoint?.displayName ?? (waypoint as any)?.display_name ?? true),
        scale,
        backgroundColorHex: String(
          waypoint?.backgroundColorHex ?? (waypoint as any)?.background_color_hex ?? "",
        ),
        backgroundOpacity: Number(
          waypoint?.backgroundOpacity ?? (waypoint as any)?.background_opacity ?? 0,
        ),
        titleFontSize: Number(
          waypoint?.fontSize ?? (waypoint as any)?.font_size ?? DEFAULT_FONT_SIZE,
        ),
        titleColorHex: String(
          waypoint?.fontColorHex ?? (waypoint as any)?.font_color_hex ?? DEFAULT_FONT_COLOR,
        ),
        titleRotationDeg: Number(
          waypoint?.nameRotationDeg ?? (waypoint as any)?.name_rotation_deg ?? 0,
        ),
        backgroundWidth,
        backgroundHeight,
      };
    })
    .filter((lift): lift is LiveMapLift => Boolean(lift));

  return {
    groupsAndBanks,
    liveLifts,
  };
}
